import moment from "moment";

function formatDateDmy(d) {
  if (!d) return "—";
  return moment(d).locale("en").format("DD/MM/YYYY");
}

function formatTime(d) {
  if (!d) return "";
  return moment(d).locale("en").format("HH:mm:ss");
}

function dayOfWeekAr(d) {
  if (!d) return "—";
  return moment(d).locale("ar").format("dddd");
}

function resolveDriver(driver) {
  if (!driver || typeof driver !== "object") return null;
  return {
    nameAr: driver.arabicName || driver.name || "—",
    employeeId: driver.employeeID != null ? String(driver.employeeID) : "—",
    mobile: driver.mobileNumber ? String(driver.mobileNumber).trim() : "—",
  };
}

function combineScheduleDateTime(dateVal, timeVal) {
  if (!dateVal) return null;
  const m = moment(dateVal);
  if (timeVal) {
    const parts = String(timeVal).split(":");
    m.set({
      hour: Number(parts[0]) || 0,
      minute: Number(parts[1]) || 0,
      second: Number(parts[2]) || 0,
      millisecond: 0,
    });
  }
  return m.isValid() ? m.toDate() : null;
}

export function buildHajjGarageTransferTripTablePrintBundle(
  input,
  { serviceSubTypeLabel = "نقل موارد", isLangRtl = true } = {},
) {
  if (!input) return null;

  const tripId = input.tripId != null ? String(input.tripId) : "—";
  const snap = input.hajjGarageTransferPrint || {};

  const start =
    input.startTime ||
    combineScheduleDateTime(input.scheduledStartDate, input.scheduledStartTime) ||
    input.scheduledStartDate;
  const end =
    input.endTime ||
    combineScheduleDateTime(input.scheduledEndDate, input.scheduledEndTime) ||
    input.scheduledEndDate ||
    start;

  const service = input.service && typeof input.service === "object" ? input.service : null;
  const serviceNameAr =
    (isLangRtl ? service?.arabicName || service?.name : service?.name || service?.arabicName) ||
    String(input.serviceText || snap.serviceText || "").trim() ||
    "—";
  const serviceId = service?._id != null ? String(service._id) : null;
  const serviceNameLine = serviceId ? `[${serviceId}] ${serviceNameAr}` : serviceNameAr;

  const d1 = resolveDriver(input.mainDriver || input.driver);
  const d2 = resolveDriver(input.secondaryDriver || input.driver2);

  const veh = input.vehicle && typeof input.vehicle === "object" ? input.vehicle : null;
  const vehicleOperationalId =
    veh?.operationalID != null
      ? String(veh.operationalID)
      : String(snap.vehicleOperationalId || input.vehicleOperationalId || "—");

  let vehicleTypeName = String(input.vehicleTypeText || snap.vehicleTypeText || "").trim();
  if (!vehicleTypeName && veh?.vehicleType) {
    const vt = veh.vehicleType;
    vehicleTypeName = (isLangRtl ? vt.arabicName || vt.name : vt.name || vt.arabicName) || "";
  }
  if (!vehicleTypeName && veh?.make && veh?.model) {
    const mk = isLangRtl ? veh.make.arabicName || veh.make.name : veh.make.name || veh.make.arabicName;
    const mdl = isLangRtl ? veh.model.arabicName || veh.model.name : veh.model.name || veh.model.arabicName;
    vehicleTypeName = [mk, mdl].filter(Boolean).join(" ");
  }
  if (!vehicleTypeName) vehicleTypeName = "—";

  const directionLabel =
    String(input.destinationText || snap.destinationText || "").trim() || "—";
  const officeLabel =
    String(input.createdByPrintName || snap.createdByPrintName || "").trim() || "—";
  const subType = String(serviceSubTypeLabel || "").trim() || "نقل موارد";

  const page = {
    tripNo: 1,
    pageIndex: 0,
    serviceNameAr: serviceNameLine,
    dayOfWeekAr: dayOfWeekAr(start),
    serviceSubType: subType,
    serviceDateFormatted: formatDateDmy(start),
    arrivalDateFormatted: formatDateDmy(end),
    clientNameAr: "—",
    driver1NameAr: d1?.nameAr || snap.driverPrintName || "—",
    driver1EmployeeId: d1?.employeeId || snap.driverEmployeeId || "—",
    driver1Mobile: d1?.mobile || snap.driverMobileNo || "—",
    driver2NameAr: d2?.nameAr || "—",
    driver2EmployeeId: d2?.employeeId || "—",
    driver2Mobile: d2?.mobile || "—",
    vehicleOperationalId,
    vehicleTypeName,
    scheduledDepartureTime: formatTime(start),
    actualDepartureTime: input.actualStartTime ? formatTime(input.actualStartTime) : "",
    scheduledArrivalTime: formatTime(end),
    actualArrivalTime: input.actualEndTime ? formatTime(input.actualEndTime) : "",
    fullServiceLabel: `${serviceNameAr} - ${subType}`,
    directionLabel,
    officeLabel,
  };

  return {
    printedAt: new Date().toISOString(),
    printedAtFormatted: moment(new Date()).locale("en").format("DD-MM-YYYY HH:mm:ss"),
    operationId: tripId,
    tripPages: [page],
  };
}

function resolvePlaceLabel(place, isLangRtl = true) {
  if (!place) return "";
  if (typeof place === "string") return String(place).trim();
  if (typeof place !== "object") return "";
  return (
    (isLangRtl ? place.arabicName || place.name : place.name || place.arabicName) || ""
  ).trim();
}

export function buildHajjGarageTransferTripTablePrintBundleFromTrip(trip, options = {}) {
  if (!trip) return null;
  const snap = trip.hajjGarageTransferPrint || {};
  const {
    destinationText: destinationOverride,
    vehicleTypeText: vehicleTypeOverride,
    serviceText: serviceTextOverride,
    createdByPrintName: createdByOverride,
    isLangRtl = true,
    ...bundleOptions
  } = options;
  const destinationFromTrip =
    resolvePlaceLabel(trip.deadheadDestinationGarage, isLangRtl) ||
    resolvePlaceLabel(trip.endLocation, isLangRtl);
  return buildHajjGarageTransferTripTablePrintBundle(
    {
      tripId: trip._id || trip.id,
      scheduledStartDate: trip.scheduledStartDate,
      scheduledStartTime: trip.scheduledStartTime,
      scheduledEndDate: trip.scheduledEndDate,
      scheduledEndTime: trip.scheduledEndTime,
      startTime: snap.startTime || trip.startTime,
      endTime: snap.endTime,
      actualStartTime: trip.actualStartTime,
      actualEndTime: trip.actualEndTime,
      service: trip.service,
      mainDriver: trip.mainDriver || trip.driver,
      secondaryDriver: trip.secondaryDriver,
      vehicle: trip.vehicle,
      hajjGarageTransferPrint: snap,
      destinationText:
        destinationOverride || snap.destinationText || destinationFromTrip,
      createdByPrintName: createdByOverride || snap.createdByPrintName,
      serviceText: serviceTextOverride || snap.serviceText,
      vehicleTypeText: vehicleTypeOverride || snap.vehicleTypeText,
    },
    { isLangRtl, ...bundleOptions },
  );
}
